import { computed, nextTick, ref, watch, type Ref } from 'vue';
import dayjs from '~/utils/dayjs';
import { formatTz, getClientTz, wallClockAt } from '~/utils/date';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useSendingCalendar } from './useSendingCalendar';
import { useMessageRules } from './useMessageRules';

interface UseQuickBlastWhenOptions {
    campaignType: Ref<'marketing' | 'transactional' | undefined>;
    modelValue: Ref<Date | string | undefined>;
    currentTab?: Ref<'now' | 'later'>;
}

interface UseQuickBlastWhenReturn {
    date: Ref<Date | string | undefined>;
    canScheduleNow: Ref<boolean>;
    initialCalendarLoading: Ref<boolean>;
    dateFormatted: Ref<string | null>;
    tzFormatted: Ref<string>;
    dateWithTzFormatted: Ref<string | null>;
    campaignTypeRestricted: Ref<boolean>;
    calculateCanScheduleNow: () => boolean;
    loadCurrentMonthCalendar: () => Promise<void>;
    roundDateToNearestFiveMinutes: (date?: Date) => void;
}

export function useQuickBlastWhen(
    options: UseQuickBlastWhenOptions,
    emit?: (event: 'update:modelValue', value: Date | string | undefined) => void
): UseQuickBlastWhenReturn {
    const { currentGroup, currentOrgUnit, onCorporate } = useCurrentGroup();
    const { isTypeRestricted } = useMessageRules();
    const { getSendingCalendar, isDateTimeAllowed, disabledDates, isMonthLoaded, findClosestAvailableTime, clearData } =
        useSendingCalendar();

    const { campaignType, modelValue, currentTab } = options;

    const date = ref<Date | string | undefined>(modelValue.value);

    const campaignTypeRestricted = computed(() => (campaignType.value ? isTypeRestricted(campaignType.value) : false));
    const clientTz = computed(() => getClientTz());

    const initialCalendarLoading = ref(false);
    const canScheduleNow = ref(onCorporate() || !campaignTypeRestricted.value);

    const calculateCanScheduleNow = (): boolean => {
        if (onCorporate() || !campaignTypeRestricted.value) {
            return true;
        }

        const time = Date.now();
        const now = wallClockAt(time, clientTz.value);
        const currentMonth = { year: now.year(), month: now.month() + 1 };

        if (!isMonthLoaded(currentMonth)) {
            return false;
        }

        const dateKey = now.format('YYYY-MM-DD');
        const isDateDisabled = disabledDates.value.some(
            (disabledDate) => wallClockAt(disabledDate, clientTz.value).format('YYYY-MM-DD') === dateKey
        );

        if (isDateDisabled) {
            return false;
        }

        const currentMinutes = now.minute();
        const roundedMinutes = Math.ceil(currentMinutes / 5) * 5;

        const roundedTime =
            roundedMinutes >= 60 ? now.add(1, 'hour').minute(0).second(0) : now.minute(roundedMinutes).second(0);

        return isDateTimeAllowed(new Date(time + roundedTime.diff(now)));
    };

    const dateFormatted = computed(() =>
        date.value && date.value !== 'now'
            ? wallClockAt(date.value, clientTz.value).format('MMM DD, YYYY [at] h:mm a')
            : null
    );
    const tzFormatted = computed(() => formatTz(dayjs().tz(clientTz.value).format('z')));
    const dateWithTzFormatted = computed(() =>
        date.value && date.value !== 'now' ? dateFormatted.value + ' ' + tzFormatted.value : null
    );

    const loadCurrentMonthCalendar = async () => {
        const groupId = currentGroup.value?.group_id;
        const orgUnitId = currentOrgUnit.value?.id;

        if (onCorporate() || !campaignType.value || !campaignTypeRestricted.value) {
            return;
        }

        initialCalendarLoading.value = true;
        const now = wallClockAt(Date.now(), clientTz.value);
        const activeMonth = { year: now.year(), month: now.month() + 1 };
        await getSendingCalendar({ groupId, orgUnitId }, campaignType.value, activeMonth);

        canScheduleNow.value = calculateCanScheduleNow();
        await nextTick();
        initialCalendarLoading.value = false;
    };

    const roundDateToNearestFiveMinutes = (dateToTransform?: Date) => {
        const transformedDate = dateToTransform ? dayjs(dateToTransform) : dayjs();
        const minutes = wallClockAt(transformedDate, clientTz.value).minute();
        const roundedMinutes = minutes >= 56 ? Math.floor(minutes / 5) * 5 : Math.ceil(minutes / 5) * 5;

        date.value = transformedDate.add(roundedMinutes - minutes, 'minute').toDate();
    };

    watch(campaignType, () => {
        clearData();
        loadCurrentMonthCalendar();
    });

    watch(
        () => modelValue.value,
        (newModelValue) => {
            date.value = newModelValue;
        },
        { immediate: true }
    );

    if (emit) {
        watch(date, (newDate) => emit('update:modelValue', newDate));
    }

    if (currentTab) {
        watch(currentTab, (newCurrentTab, oldCurrentTab) => {
            if (newCurrentTab === 'now') {
                date.value = 'now';
            } else if (newCurrentTab === 'later') {
                if (!onCorporate() && campaignTypeRestricted.value) {
                    if (oldCurrentTab === 'now' && canScheduleNow.value) {
                        date.value = findClosestAvailableTime();
                    } else {
                        date.value = undefined;
                    }
                } else {
                    const now = dayjs();
                    const minutes = wallClockAt(now, clientTz.value).minute();
                    const roundedMinutes = Math.ceil(minutes / 5) * 5;
                    date.value = now.add(roundedMinutes - minutes, 'minute').toDate();
                }
            }
        });
    }

    return {
        date,
        canScheduleNow,
        initialCalendarLoading,
        dateFormatted,
        tzFormatted,
        dateWithTzFormatted,
        campaignTypeRestricted,
        calculateCanScheduleNow,
        loadCurrentMonthCalendar,
        roundDateToNearestFiveMinutes,
    };
}
